import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Presentation, Edit } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { markFreeCourseDownloadUsed } from "@/lib/freeTier";
import SlideEditor from "./SlideEditor";

interface SlideDeck {
  id: string;
  version: number;
  min_slides: number | null;
  max_slides: number | null;
  level: string | null;
  pptx_path: string | null;
  slide_deck_json: any;
  created_at: string;
}

interface SlideDecksViewProps {
  decks: SlideDeck[];
  onUpdate: () => void;
}

const SlideDecksView = ({ decks, onUpdate }: SlideDecksViewProps) => {
  const { toast } = useToast();
  const [editingDeck, setEditingDeck] = useState<SlideDeck | null>(null);

  const handleDownload = async (deck: SlideDeck) => {
    if (!deck.pptx_path) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No file available for download",
      });
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from('slide-decks')
        .download(deck.pptx_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `slides-v${deck.version}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Slide deck downloaded successfully",
      });
      await markFreeCourseDownloadUsed();
    } catch (error) {
      console.error('Download error:', error);
      toast({
        variant: "destructive",
        title: "Error downloading",
        description: error instanceof Error ? error.message : "Download failed",
      });
    }
  };

  if (decks.length === 0) {
    return (
      <p className="text-muted-foreground">
        No slide decks generated yet. Click "Generate Slides" to create one.
      </p>
    );
  }

  if (editingDeck) {
    return (
      <SlideEditor
        deckId={editingDeck.id}
        initialDeck={editingDeck.slide_deck_json}
        onClose={() => setEditingDeck(null)}
        onSave={() => {
          setEditingDeck(null);
          onUpdate();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {decks.map((deck) => (
        <Card key={deck.id}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Presentation className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg">Version {deck.version}</CardTitle>
                {deck.level && (
                  <Badge variant="secondary" className="capitalize">
                    {deck.level}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setEditingDeck(deck)} size="sm" variant="outline">
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button onClick={() => handleDownload(deck)} size="sm">
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>
            </div>
            <CardDescription>
              Generated on {new Date(deck.created_at).toLocaleString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <div>
                <span className="font-semibold">Slides:</span>{" "}
                {deck.slide_deck_json?.slides?.length || 0}
              </div>
              {deck.min_slides && deck.max_slides && (
                <div>
                  <span className="font-semibold">Target Range:</span>{" "}
                  {deck.min_slides} - {deck.max_slides}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default SlideDecksView;
